import React, { useState, useEffect } from 'react';
import { RichTextEditor } from './RichTextEditor';
import { RichTextContent } from '../../types/good-things';
import { convertPlainTextToRichText, extractPlainText } from '../../utils/richTextUtils';

interface NoteFieldProps {
  value: string;
  onChange: (value: string) => void;
  onRichTextChange?: (richText: RichTextContent) => void;
  richTextValue?: RichTextContent;
  required?: boolean;
  placeholder?: string;
  className?: string;
}

export const NoteField: React.FC<NoteFieldProps> = React.memo(({
  value,
  onChange,
  onRichTextChange,
  richTextValue,
  required = true,
  placeholder = "Enter the good thing note...",
  className = ""
}) => {
  // Use internal rich text state to preserve formatting
  const [internalRichText, setInternalRichText] = useState<RichTextContent>(() => {
    // Initialize with provided richTextValue or convert from plain text
    return richTextValue || convertPlainTextToRichText(value);
  });

  // Update internal rich text when richTextValue prop changes
  useEffect(() => {
    if (richTextValue) {
      setInternalRichText(richTextValue);
    } else if (value && !richTextValue) {
      // Only convert from plain text if we don't have rich text data
      setInternalRichText(convertPlainTextToRichText(value));
    } else if (!value && !richTextValue) {
      // Clear the rich text when both value and richTextValue are empty
      setInternalRichText(convertPlainTextToRichText(''));
    }
  }, [richTextValue, value]);

  // Handle rich text changes and convert back to plain text for backward compatibility
  const handleRichTextChange = (richText: RichTextContent) => {
    setInternalRichText(richText);
    const plainText = extractPlainText(richText);
    onChange(plainText);
    
    // Also pass the rich text data to parent if callback is provided
    if (onRichTextChange) {
      onRichTextChange(richText);
    }
  };

  return (
    <RichTextEditor
      value={internalRichText}
      onChange={handleRichTextChange}
      placeholder={placeholder}
      className={className}
      required={required}
    />
  );
});

NoteField.displayName = 'NoteField';

export default NoteField;